/**
 * @docs docs/features/website-navigation-shell
 */

/*----------------------------------
- MOBILE SITE MENU
----------------------------------*/

import React from 'react';

// The 480px cut, not the 1370px master: the slot is 190px wide at most, and the build serves
// the largest generated size as `src`, so the master shipped 1300px for every page view.
import SignatureLockup from '@/client/assets/identity/signature-lockup-ink-480.png';
import Icon from '@/client/components/ui/icon/Icon';
import {
    DOMAINS_MENU_COLUMN_TITLES,
    type LandingHeaderDomainsNavItem,
} from '@/client/catalogs/landing/pageContent';
import { LANDING_MARKET_MENU_ITEMS } from '@/client/catalogs/landing/marketMenu';
import { LANDING_MENU_PANEL_COPY } from '@/client/catalogs/landing/menuPanels';
import { LANDING_TOOLS_MENU_ITEMS } from '@/client/catalogs/landing/toolsMenu';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import type { Links } from '../../types';
import DomainsMenuChip from './DomainsMenuChip';
import MenuRow from './MenuRow';
import MobileMenuSection from './MobileMenuSection';
import { MOBILE_MENU_SECTION_LABEL_CLASS, formatTldLabel, getDomainsMenuColumnItems } from './menuShared';
import { NAV_SCREENER_ITEM, type MenuNavigateHandler } from './navClick';

/*----------------------------------
- TYPES
----------------------------------*/

export type MobileSiteMenuSectionId = 'domains' | 'market' | 'tools';

type MobileSiteMenuProps = {
    isOpen: boolean;
    links: Links;
    /** Null while the public Domains hub is unpublished, which drops the whole section. */
    domainsMenu: LandingHeaderDomainsNavItem | null;
    brandHref: string;
    isMember: boolean;
    onBrandClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    onClose: () => void;
    /**
     * Follows a menu link: records it, then closes the dialog. Log in and GitHub keep their
     * own handlers because they already emit a conversion trigger and a GitHub event.
     */
    onNavigate: MenuNavigateHandler;
    onLogin: () => void;
    onGithub: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const PLAIN_ROW_CLASS = 'flex items-center justify-between px-1 py-3 text-base font-semibold text-[#4a4d4e]';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';
const HEADLINE_ROW_CLASS = 'flex items-center justify-between gap-3 border-b border-[#e6e2da] px-1 py-[18px]';
const HEADLINE_LABEL_CLASS = 'text-2xl font-semibold leading-[1.1] tracking-[-0.03em]';
const HEADLINE_GLYPH_CLASS =
    'inline-flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-[#edeae2]';

const CONTACT_HREF = `${LANDING_WEBSITE_PAGE_ROUTES.team}#contact`;

export const MOBILE_SITE_MENU_SECTION_IDS: Record<MobileSiteMenuSectionId, string> = {
    domains: 'landing-mobile-domains-menu',
    market: 'landing-mobile-market-menu',
    tools: 'landing-mobile-tools-menu',
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Full-screen phone navigation, mirroring the desktop bar: the three megamenus become
 * in-place accordions, one open at a time, then Pricing, Log in, GitHub and Contact as
 * plain rows. It sits under the action bar (z-40) on purpose: Start with my brief stays reachable
 * while the menu is open. Log in stays here even though the desktop bar dropped it: the
 * phone has no other in-menu way back into an existing account.
 */
export default function MobileSiteMenu({
    isOpen,
    links,
    domainsMenu,
    brandHref,
    isMember,
    onBrandClick,
    onClose,
    onNavigate,
    onLogin,
    onGithub,
}: MobileSiteMenuProps) {
    const dialogRef = React.useRef<HTMLDivElement | null>(null);
    const [openSection, setOpenSection] = React.useState<MobileSiteMenuSectionId | null>(null);
    const toggleSection = (id: MobileSiteMenuSectionId) =>
        setOpenSection((current) => (current === id ? null : id));

    // Escape closes, Tab cycles inside the dialog. Collapsed panels are `hidden`, which
    // already removes their links from the tab order, so the live set is read per keypress.
    React.useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') return onClose();
            if (event.key !== 'Tab') return;

            const focusable = Array.from(
                dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) || [],
            ).filter((element) => element.offsetParent !== null);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) return;

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    return (
        <div
            ref={dialogRef}
            class="fixed inset-0 z-[35] flex flex-col bg-[#f8f6f1] text-[#1b1d1e] lg:hidden"
            hidden={!isOpen}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            data-testid="mobile-site-menu"
        >
            <div class="flex flex-none items-center justify-between border-b border-[rgba(27,29,30,0.06)] px-4 pb-2.5 pt-[max(10px,env(safe-area-inset-top))]">
                <a href={brandHref} onClick={onBrandClick} class="flex h-11 items-center" aria-label="Unique Domains home">
                    <img src={SignatureLockup} alt="" width="158" height="30" class="h-[30px] w-auto" />
                </a>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close navigation menu"
                    class="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#1b1d1e] text-white"
                >
                    <Icon name="x" size={18} strokeWidth={2.2} />
                </button>
            </div>

            <nav aria-label="Site navigation" class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-32 pt-1.5">
                {domainsMenu ? (
                    <MobileMenuSection
                        id={MOBILE_SITE_MENU_SECTION_IDS.domains}
                        label="Domains"
                        isOpen={openSection === 'domains'}
                        onToggle={() => toggleSection('domains')}
                        testId="mobile-header-domains-disclosure"
                    >
                        <div class="flex flex-col gap-5" data-testid="mobile-domains-menu">
                            <div>
                                <div class={MOBILE_MENU_SECTION_LABEL_CLASS}>{DOMAINS_MENU_COLUMN_TITLES.lists}</div>
                                <ul class="flex flex-col gap-0.5">
                                    {getDomainsMenuColumnItems(domainsMenu, DOMAINS_MENU_COLUMN_TITLES.lists).map(
                                        (item) => (
                                            <li key={item.label}>
                                                <MenuRow
                                                    href={item.href || links.screener}
                                                    label={item.label}
                                                    description={item.description}
                                                    iconName={item.iconName}
                                                    density="mobile"
                                                    onClick={() => onNavigate(item.label, item.href || links.screener)}
                                                />
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>
                            <div>
                                <div class={MOBILE_MENU_SECTION_LABEL_CLASS}>
                                    <abbr title="Top-level domains: the ending, like .com or .ai" class="ud-abbr">
                                        {DOMAINS_MENU_COLUMN_TITLES.endings}
                                    </abbr>
                                </div>
                                <div class="flex flex-wrap gap-2 px-1">
                                    {getDomainsMenuColumnItems(domainsMenu, DOMAINS_MENU_COLUMN_TITLES.endings).map(
                                        (item) => (
                                            <DomainsMenuChip
                                                key={item.label}
                                                item={item}
                                                fallbackHref={links.screener}
                                                isTld
                                                onClick={() =>
                                                    onNavigate(formatTldLabel(item.label), item.href || links.screener)}
                                            />
                                        ),
                                    )}
                                    <a
                                        href={LANDING_WEBSITE_PAGE_ROUTES.tldHub}
                                        onClick={() => onNavigate(LANDING_MENU_PANEL_COPY.domains.allTldsLabel, LANDING_WEBSITE_PAGE_ROUTES.tldHub)}
                                        class="inline-flex min-h-[38px] items-center gap-[7px] rounded-full border border-[#c9c8c2] bg-white px-[13px] text-[13.5px] font-semibold text-[#1b1d1e]"
                                        data-testid="mobile-domains-menu-all-tlds"
                                    >
                                        {LANDING_MENU_PANEL_COPY.domains.allTldsLabel}
                                    </a>
                                </div>
                            </div>
                            <div>
                                <div class={MOBILE_MENU_SECTION_LABEL_CLASS}>{DOMAINS_MENU_COLUMN_TITLES.sectors}</div>
                                <div class="flex flex-wrap gap-2 px-1">
                                    {getDomainsMenuColumnItems(domainsMenu, DOMAINS_MENU_COLUMN_TITLES.sectors).map(
                                        (item) => (
                                            <DomainsMenuChip
                                                key={item.label}
                                                item={item}
                                                fallbackHref={links.screener}
                                                onClick={() => onNavigate(item.label, item.href || links.screener)}
                                            />
                                        ),
                                    )}
                                    {/* The one way out of the two chip lists, since the panel header's
                                        screener button is a desktop-only affordance. */}
                                    <a
                                        href={links.screener}
                                        onClick={() => onNavigate(NAV_SCREENER_ITEM, links.screener)}
                                        class="inline-flex min-h-[38px] items-center gap-[7px] rounded-full border border-[#1b1d1e] bg-[#1b1d1e] px-[13px] text-[13.5px] font-semibold text-white"
                                    >
                                        Open the screener
                                        <Icon name="arrow-right" size={13} strokeWidth={2.4} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </MobileMenuSection>
                ) : null}

                <MobileMenuSection
                    id={MOBILE_SITE_MENU_SECTION_IDS.market}
                    label="Market"
                    isOpen={openSection === 'market'}
                    onToggle={() => toggleSection('market')}
                    testId="mobile-header-market-disclosure"
                >
                    <ul class="flex flex-col gap-0.5" data-testid="mobile-market-menu">
                        {LANDING_MARKET_MENU_ITEMS.map((item) => (
                            <li key={item.id}>
                                <MenuRow
                                    href={item.href}
                                    status={item.status}
                                    label={item.label}
                                    description={item.description}
                                    iconName={item.iconName}
                                    density="mobile"
                                    onClick={() => item.href && onNavigate(item.id, item.href)}
                                />
                            </li>
                        ))}
                    </ul>
                </MobileMenuSection>

                <MobileMenuSection
                    id={MOBILE_SITE_MENU_SECTION_IDS.tools}
                    label="Tools"
                    isOpen={openSection === 'tools'}
                    onToggle={() => toggleSection('tools')}
                    testId="mobile-header-tools-disclosure"
                >
                    <ul class="flex flex-col gap-0.5" data-testid="mobile-tools-menu">
                        {LANDING_TOOLS_MENU_ITEMS.map((item) => (
                            <li key={item.id}>
                                <MenuRow
                                    href={item.href}
                                    status={item.status}
                                    label={item.label}
                                    description={item.description}
                                    iconName={item.iconName}
                                    tile="ink"
                                    density="mobile"
                                    onClick={() => item.href && onNavigate(item.id, item.href)}
                                />
                            </li>
                        ))}
                    </ul>
                </MobileMenuSection>

                <a href={links.pricing} onClick={() => onNavigate('pricing', links.pricing)} class={HEADLINE_ROW_CLASS}>
                    <span class={HEADLINE_LABEL_CLASS}>Pricing</span>
                    <span aria-hidden="true" class={HEADLINE_GLYPH_CLASS}>
                        <Icon name="arrow-right" size={16} strokeWidth={2.2} />
                    </span>
                </a>

                <div class="flex flex-col gap-0.5 pt-3.5">
                    <a href={links.signIn} onClick={onLogin} hidden={isMember} class={PLAIN_ROW_CLASS}>
                        Log in
                        <Icon name="arrow-right" size={16} strokeWidth={2.2} className="text-[#6a695e]" />
                    </a>
                    <a
                        href={links.githubLists}
                        target="_blank"
                        rel="noreferrer"
                        onClick={onGithub}
                        class={PLAIN_ROW_CLASS}
                    >
                        GitHub lists
                        <Icon name="arrow-up-right" size={16} strokeWidth={2.2} className="text-[#6a695e]" />
                    </a>
                    <a href={CONTACT_HREF} onClick={() => onNavigate('contact', CONTACT_HREF)} class={PLAIN_ROW_CLASS}>
                        Contact
                        <Icon name="arrow-right" size={16} strokeWidth={2.2} className="text-[#6a695e]" />
                    </a>
                </div>

                <p class="mx-1 mt-[22px] text-xs leading-normal text-[#66696b]">
                    © {new Date().getFullYear()} Unique Domains · Where domain decisions are made.
                </p>
            </nav>
        </div>
    );
}
